import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import { CircleDollarSignIcon, InfoIcon } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  IBrazilianCompanyIssuerDividendPeriodicity,
  type IBrazilianCompanyIssuerMandatoryDividend,
} from "types/graphqlTypes";

interface MandatoryDividendProps {
  mandatoryDividend: Pick<
    IBrazilianCompanyIssuerMandatoryDividend,
    "displayText" | "periodicity" | "periodicityDescription" | "remarks" | "hasPreferredSharesDividendAdvantage"
  >;
}

export default function MandatoryDividend({ mandatoryDividend }: MandatoryDividendProps) {
  const intl = useIntl();

  return (
    <section className="w-full shrink-0 self-start rounded-xl bg-orange-50/40 p-4 dark:bg-orange-300/10">
      <div className="flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <h2 className="flex min-w-0 items-center gap-2 text-lg font-bold text-slate-800 dark:text-slate-200">
          <CircleDollarSignIcon aria-hidden={true} className="size-4 shrink-0 text-orange-500 dark:text-orange-400" />
          <FormattedMessage defaultMessage="Política de Dividendos" id="b+WHJh" />
        </h2>

        <div className="flex flex-wrap items-center gap-1.5">
          <PeriodicityChip periodicity={mandatoryDividend.periodicity} />

          {mandatoryDividend.hasPreferredSharesDividendAdvantage && (
            <span className="inline-flex whitespace-nowrap rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold leading-5 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
              <FormattedMessage defaultMessage="PNs com prioridade" id="xkLhf3" />
            </span>
          )}
        </div>
      </div>

      <div className="mt-3 min-w-0 space-y-2 text-pretty text-sm leading-7 text-slate-600 [overflow-wrap:anywhere] dark:text-slate-300">
        <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2">
          <strong className="mt-1 inline-flex whitespace-nowrap rounded-md bg-slate-200/70 px-1.5 py-0.5 text-xs font-bold leading-4 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            <FormattedMessage defaultMessage="Regra" id="nfg2ty" />
          </strong>
          <p className="min-w-0">
            {mandatoryDividend.displayText}
            {mandatoryDividend.remarks && (
              <span className="ml-1.5 inline-flex align-middle">
                <SimpleTextTooltip
                  className="max-w-xs text-sm"
                  content={<p className="font-normal leading-6">{mandatoryDividend.remarks}</p>}
                >
                  <button
                    type="button"
                    aria-label={intl.formatMessage({
                      defaultMessage: "Mais detalhes sobre a política de dividendos",
                      id: "vfMqVI",
                    })}
                    className="shrink-0 rounded-full text-slate-400 outline-none transition-colors duration-150 ease-out hover:text-orange-500 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 dark:text-slate-500 dark:hover:text-orange-300 dark:focus-visible:ring-offset-slate-800"
                  >
                    <InfoIcon size={15} strokeWidth={2} />
                  </button>
                </SimpleTextTooltip>
              </span>
            )}
          </p>
        </div>
        {mandatoryDividend.periodicity !== IBrazilianCompanyIssuerDividendPeriodicity.None && (
          <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2">
            <strong className="mt-1 inline-flex whitespace-nowrap rounded-md bg-slate-200/70 px-1.5 py-0.5 text-xs font-bold leading-4 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              <FormattedMessage defaultMessage="Periodicidade" id="lpKc9+" />
            </strong>
            <p className="min-w-0">{mandatoryDividend.periodicityDescription}</p>
          </div>
        )}
      </div>
    </section>
  );
}

function PeriodicityChip({ periodicity }: { periodicity: IBrazilianCompanyIssuerDividendPeriodicity }) {
  const details = getPeriodicityDetails(periodicity);

  if (!details) {
    return null;
  }

  return (
    <span
      className={classNames(
        "inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold leading-5",
        details.className,
      )}
    >
      {details.label}
    </span>
  );
}

function getPeriodicityDetails(periodicity: IBrazilianCompanyIssuerDividendPeriodicity) {
  switch (periodicity) {
    case IBrazilianCompanyIssuerDividendPeriodicity.Annual:
      return {
        label: (
          <FormattedMessage
            defaultMessage="Anual"
            description="Periodicidade para distribuição dos dividendos"
            id="v0bpXX"
          />
        ),
        className: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
      };
    case IBrazilianCompanyIssuerDividendPeriodicity.Semiannual:
      return {
        label: (
          <FormattedMessage
            defaultMessage="Semestral"
            description="Periodicidade para distribuição dos dividendos"
            id="521Yp1"
          />
        ),
        className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
      };
    case IBrazilianCompanyIssuerDividendPeriodicity.Quarterly:
      return {
        label: (
          <FormattedMessage
            defaultMessage="Trimestral"
            description="Periodicidade para distribuição dos dividendos"
            id="pKAHhM"
          />
        ),
        className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
      };
    case IBrazilianCompanyIssuerDividendPeriodicity.Monthly:
      return {
        label: (
          <FormattedMessage
            defaultMessage="Mensal"
            description="Periodicidade para distribuição dos dividendos"
            id="Up/Fj3"
          />
        ),
        className: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
      };
    case IBrazilianCompanyIssuerDividendPeriodicity.None:
    default:
      return null;
  }
}
